import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import AdminLayout from "../../../components/layouts/adminLayout";
import AdminPageContainer from "../../../components/AdminPageContainer";
import LicenseConfigureServices from "../../../services/API/LicenseConfiguration";
import LicenseMasterServices from "../../../services/API/LicenseMaster";
import { MultiSelect } from "primereact/multiselect";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import { toast } from "react-toastify";
import FullScreenLoader from "../../../components/FullScreenLoader";

const CONFIG_TYPES = {
  location: {
    label: "States",
    relationKey: "states",
    fetch: (params) => LicenseMasterServices.getAllStates(params),
  },
  business_type: {
    label: "Business Types",
    relationKey: "industry_types",
    fetch: (params) => LicenseMasterServices.getAllIndustry(params),
  },
  company_type: {
    label: "Company Types",
    relationKey: "company_types",
    fetch: (params) => LicenseMasterServices.getAllCompanyType(params),
  },
};

export default function ConfigureLicensePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [license, setLicense] = useState(null);
  const [slabPrice, setSlabPrice] = useState("");
  const [configType, setConfigType] = useState("");
  const [optionsList, setOptionsList] = useState([]);
  const [optionsCache, setOptionsCache] = useState({});
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [priceRows, setPriceRows] = useState([{ values: [], price: "" }]);
  const [flatPrice, setFlatPrice] = useState("");
  const [otherRows, setOtherRows] = useState([{ name: "", price: "" }]);
  const [pricingExists, setPricingExists] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const getLicense = async () => {
      try {
        const { data } = await LicenseConfigureServices.editLicense(id);
        setLicense(data);
      } catch (er) {
        toast.error(er);
      }
    };
    if (id) getLicense();
  }, [id]);

  useEffect(() => {
    const getPricing = async () => {
      try {
        setPricingLoading(true);
        const { data } = await LicenseConfigureServices.editPricing(id);
        if (!data) return;

        setPricingExists(true);
        setSlabPrice(data.slab_price ? "yes" : "no");

        if (!data.slab_price) {
          setFlatPrice(data.flat_price ?? "");
          return;
        }

        setConfigType(data.type);
        if (data.type === "other") {
          setOtherRows(
            (data.slabs || []).map((slab) => ({
              name: slab.other_name,
              price: slab.price,
            }))
          );
          return;
        }

        const { fetch, relationKey } = CONFIG_TYPES[data.type];
        const { data: options } = await fetch({ active: 1 });
        setOptionsList(options || []);
        setOptionsCache((prev) => ({ ...prev, [data.type]: options || [] }));

        const optionById = Object.fromEntries(
          (options || []).map((option) => [option.id, option])
        );
        setPriceRows(
          (data.slabs || []).map((slab) => ({
            values: (slab[relationKey] || []).map(
              (value) => optionById[value.id] || value
            ),
            price: slab.price,
          }))
        );
      } catch (er) {
        // no existing pricing config yet
      } finally {
        setPricingLoading(false);
      }
    };
    if (id) getPricing();
  }, [id]);

  const handleSlabPriceChange = (value) => {
    setSlabPrice(value);
    setConfigType("");
    setOptionsList([]);
    setPriceRows([{ values: [], price: "" }]);
    setFlatPrice("");
  };

  const handleConfigTypeChange = async (type) => {
    setConfigType(type);
    setPriceRows([{ values: [], price: "" }]);
    setOtherRows([{ name: "", price: "" }]);

    if (!type || !CONFIG_TYPES[type]) {
      setOptionsList([]);
      return;
    }
    if (optionsCache[type]) {
      setOptionsList(optionsCache[type]);
      return;
    }
    setOptionsList([]);
    setOptionsLoading(true);
    try {
      const { data } = await CONFIG_TYPES[type].fetch({ active: 1 });
      setOptionsList(data || []);
      setOptionsCache((prev) => ({ ...prev, [type]: data || [] }));
    } finally {
      setOptionsLoading(false);
    }
  };

  const getAvailableOptions = (rowIndex) => {
    const selectedElsewhere = priceRows
      .filter((_, idx) => idx !== rowIndex)
      .flatMap((row) => row.values.map((value) => value.id));
    return optionsList.filter((option) => !selectedElsewhere.includes(option.id));
  };

  const handleRowValuesChange = (rowIndex, values) => {
    setPriceRows((prev) =>
      prev.map((row, idx) => (idx === rowIndex ? { ...row, values } : row))
    );
  };

  const handleRowPriceChange = (rowIndex, price) => {
    setPriceRows((prev) =>
      prev.map((row, idx) => (idx === rowIndex ? { ...row, price } : row))
    );
  };

  const handleAddMore = () => {
    setPriceRows((prev) => [...prev, { values: [], price: "" }]);
  };

  const handleRemoveRow = (rowIndex) => {
    setPriceRows((prev) => prev.filter((_, idx) => idx !== rowIndex));
  };

  const handleOtherNameChange = (rowIndex, name) => {
    setOtherRows((prev) =>
      prev.map((row, idx) => (idx === rowIndex ? { ...row, name } : row))
    );
  };

  const handleOtherPriceChange = (rowIndex, price) => {
    setOtherRows((prev) =>
      prev.map((row, idx) => (idx === rowIndex ? { ...row, price } : row))
    );
  };

  const handleAddOtherRow = () => {
    setOtherRows((prev) => [...prev, { name: "", price: "" }]);
  };

  const handleRemoveOtherRow = (rowIndex) => {
    setOtherRows((prev) => prev.filter((_, idx) => idx !== rowIndex));
  };

  const handleSubmit = async () => {
    if (!slabPrice) {
      toast.error("Please select Slab Price");
      return;
    }

    if (slabPrice === "no" && flatPrice === "") {
      toast.error("Please enter price");
      return;
    }

    if (slabPrice === "no" && Number(flatPrice) <= 0) {
      toast.error("Price must be a positive value");
      return;
    }

    if (slabPrice === "yes") {
      if (!configType) {
        toast.error("Please select type");
        return;
      }
      if (configType === "other") {
        const incompleteOther = otherRows.some(
          (row) => !row.name || row.price === ""
        );
        if (incompleteOther) {
          toast.error("Please enter value and price for all rows");
          return;
        }
        const otherNames = otherRows.map((row) =>
          row.name.trim().toLowerCase()
        );
        const hasDuplicateOther = otherNames.some(
          (name, idx) => otherNames.indexOf(name) !== idx
        );
        if (hasDuplicateOther) {
          toast.error("Duplicate values are not allowed");
          return;
        }
        const hasNonPositiveOther = otherRows.some(
          (row) => Number(row.price) <= 0
        );
        if (hasNonPositiveOther) {
          toast.error("Price must be a positive value");
          return;
        }
      } else {
        const incomplete = priceRows.some(
          (row) => row.values.length === 0 || row.price === ""
        );
        if (incomplete) {
          toast.error(
            `Please select ${CONFIG_TYPES[configType].label} and enter price for all rows`
          );
          return;
        }
        const hasNonPositive = priceRows.some(
          (row) => Number(row.price) <= 0
        );
        if (hasNonPositive) {
          toast.error("Price must be a positive value");
          return;
        }
      }
    }

    const payload = { slab_price: slabPrice };
    if (slabPrice === "no") {
      payload.flat_price = flatPrice;
    } else {
      payload.type = configType;
      if (configType === "other") {
        payload.other_slabs = otherRows.map((row) => ({
          name: row.name,
          price: row.price,
        }));
      } else {
        payload.slabs = priceRows.map((row) => ({
          ids: row.values.map((value) => value.id),
          price: row.price,
        }));
      }
    }

    try {
      setIsSubmitting(true);
      const response = pricingExists
        ? await LicenseConfigureServices.updatePricing(id, payload)
        : await LicenseConfigureServices.savePricing(id, payload);
      if (response) {
        setPricingExists(true);
        toast.success("Configuration saved successfully");
        navigate("/system-admin/license-configuration");
      }
    } catch (er) {
      toast.error(er);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout test3="active">
      <AdminPageContainer>
        <FullScreenLoader show={pricingLoading} title={"Loading"} />
        <div id="main" role="main">
          <div id="content">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a
                  style={{ color: "blue", cursor: "pointer" }}
                  onClick={() => navigate("/system-admin/license-configuration")}
                  onKeyDown={() => navigate("/system-admin/license-configuration")}
                >
                  License Configuration Settings
                </a>
              </li>
              <li className="breadcrumb-item active">
                Configure{license?.name ? ` - ${license.name}` : ""}
              </li>
            </ol>

            <div
              className="card card-sm card-shadow mb-3"
              style={{ minHeight: "80vh" }}
            >
              <div className="card-body">
                <div className="form-group mb-4">
                  <label htmlFor="slabPrice">Slab Price</label>
                  <select
                    className="form-control mb-3 w-50"
                    id="slabPrice"
                    value={slabPrice}
                    onChange={(e) => handleSlabPriceChange(e.target.value)}
                  >
                    <option value="">Select Value</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                {slabPrice === "yes" && (
                  <div className="form-group mb-4">
                    <label htmlFor="configType">Select Type</label>
                    <select
                      className="form-control mb-3 w-50"
                      id="configType"
                      value={configType}
                      onChange={(e) => handleConfigTypeChange(e.target.value)}
                    >
                      <option value="">Select Value</option>
                      <option value="location">Location</option>
                      <option value="business_type">Business Type</option>
                      <option value="company_type">Company Type</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                )}

                {slabPrice === "yes" && configType === "other" && (
                  <div className="form-group">
                    <label htmlFor="otherValues">Text</label>
                    {otherRows.map((row, idx) => {
                      const isLastRow = idx === otherRows.length - 1;
                      const hasName = !!row.name;
                      return (
                        <div
                          className="d-flex align-items-center gap-3 mb-3"
                          key={idx}
                        >
                          <input
                            type="text"
                            className="form-control w-50 me-3"
                            placeholder="Enter Value"
                            value={row.name}
                            onChange={(e) =>
                              handleOtherNameChange(idx, e.target.value)
                            }
                          />
                          <input
                            type="number"
                            className="form-control"
                            style={{ maxWidth: "150px" }}
                            placeholder="Price"
                            value={row.price}
                            disabled={!hasName}
                            onChange={(e) =>
                              handleOtherPriceChange(idx, e.target.value)
                            }
                          />
                          {isLastRow && (
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              title="Add More"
                              disabled={!hasName}
                              onClick={handleAddOtherRow}
                            >
                              <i className="fa fa-plus-circle fa-fw"></i>
                            </button>
                          )}
                          {otherRows.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              title="Remove"
                              onClick={() => handleRemoveOtherRow(idx)}
                            >
                              <i className="fa fa-trash fa-fw"></i>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {slabPrice === "no" && (
                  <div className="form-group mb-4">
                    <label htmlFor="flatPrice">Price</label>
                    <input
                      type="number"
                      className="form-control"
                      style={{ maxWidth: "200px" }}
                      id="flatPrice"
                      placeholder="Price"
                      value={flatPrice}
                      onChange={(e) => setFlatPrice(e.target.value)}
                    />
                  </div>
                )}

                {slabPrice === "yes" && configType && CONFIG_TYPES[configType] && (
                  <div className="form-group">
                    <label htmlFor="configValues">
                      Select {CONFIG_TYPES[configType].label}
                    </label>
                    {priceRows.map((row, idx) => {
                      const isLastRow = idx === priceRows.length - 1;
                      const hasValues = row.values.length > 0;
                      return (
                        <div
                          className="d-flex align-items-center gap-3 mb-3"
                          key={idx}
                        >
                          <MultiSelect
                            value={row.values}
                            onChange={(e) =>
                              handleRowValuesChange(idx, e.value)
                            }
                            options={getAvailableOptions(idx)}
                            optionLabel="name"
                            placeholder={`Select ${CONFIG_TYPES[configType].label}`}
                            display="chip"
                            filter
                            scrollHeight="200px"
                            className="w-50 me-3"
                            loading={optionsLoading}
                          />
                          <input
                            type="number"
                            className="form-control"
                            style={{ maxWidth: "150px" }}
                            placeholder="Price"
                            value={row.price}
                            disabled={!hasValues}
                            onChange={(e) =>
                              handleRowPriceChange(idx, e.target.value)
                            }
                          />
                          {isLastRow && (
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              title="Add More"
                              disabled={!hasValues}
                              onClick={handleAddMore}
                            >
                              <i className="fa fa-plus-circle fa-fw"></i>
                            </button>
                          )}
                          {priceRows.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              title="Remove"
                              onClick={() => handleRemoveRow(idx)}
                            >
                              <i className="fa fa-trash fa-fw"></i>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4"
                    onClick={() => navigate("/system-admin/license-configuration")}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AdminPageContainer>
    </AdminLayout>
  );
}
